import type { GitpodAPI } from "@/api";
import { type EnvironmentOpsServiceClient, useEnvironmentOpsService } from "@/hooks/use-environment-ops-service";
import { useIncrementEnvironmentStartCount } from "@/hooks/use-environment-start-count";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { useSegmentTrack } from "@/hooks/use-segment";
import { defaultRefetchLists, defaultRetry, defaultThrowOnError } from "@/queries/errors";
import { autoPaginate } from "@/queries/pagination";
import { keyWithPrincipal } from "@/queries/principal-key";
import type { PlainProject, PlainProjectEnvironmentClass } from "@/queries/project-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { MaximumEnvironmentLifetimeExceededError } from "@/utils/errors";
import { mimeTypeFromPath } from "@/utils/mime-type";
import { type PartialMessage, type PlainMessage, Timestamp, toPlainMessage } from "@bufbuild/protobuf";
import { Code, ConnectError } from "@connectrpc/connect";
import {
    type QueryClient,
    keepPreviousData,
    skipToken,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import {
    type Environment,
    type EnvironmentInitializer,
    type EnvironmentInitializer_Spec,
    EnvironmentPhase,
    EnvironmentRole,
    EnvironmentSpec,
    EnvironmentSpec_Content,
    type EnvironmentSpec_DevContainer,
    EnvironmentSpec_Machine,
    GitInitializer_CloneTargetMode,
    ListEnvironmentsRequest_Filter,
    ListEnvironmentsRequest_ArchivalStatus,
    type UnarchiveEnvironmentResponse,
    UpdateEnvironmentRequest,
    type GitInitializer,
} from "gitpod-next-api/gitpod/v1/environment_pb";
import { ResourceOperation, type WatchEventsResponse } from "gitpod-next-api/gitpod/v1/event_pb";
import type { RunnerKind } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import {
    GetEnvironmentClassRequest,
    type EnvironmentClass,
    type ListEnvironmentClassesRequest_Filter,
} from "gitpod-next-api/gitpod/v1/runner_configuration_pb.ts";
import type { CheckAuthenticationForHostResponse } from "gitpod-next-api/gitpod/v1/runner_pb";
import { useCallback, useRef } from "react";
import { v4 as uuidv4 } from "uuid";

export const environmentNotDeletedPhases = [
    EnvironmentPhase.UNSPECIFIED,
    EnvironmentPhase.CREATING,
    EnvironmentPhase.STARTING,
    EnvironmentPhase.RUNNING,
    EnvironmentPhase.UPDATING,
    EnvironmentPhase.STOPPING,
    EnvironmentPhase.STOPPED,
    EnvironmentPhase.DELETING,
];

export type PlainEnvironment = PlainMessage<Environment>;

export const toPlainEnvironment = (environment: Environment): PlainEnvironment => {
    return toPlainMessage(environment);
};

export const environmentQueryKey = {
    list: (filter?: PartialMessage<ListEnvironmentsRequest_Filter>) =>
        keyWithPrincipal(["environments", "list", filter ? filter : "all"]),
    listInventory: (filter: Record<string, string | number | undefined>) =>
        keyWithPrincipal(["environments", "inventory", "list", filter]),
    runnerEnvironments: (runnerId: string) => keyWithPrincipal(["environments", "runner", "list", runnerId]),
    listClasses: (filter: PartialMessage<ListEnvironmentClassesRequest_Filter>) =>
        keyWithPrincipal(["environments", "classes", "list", filter]),
    get: (environmentId?: string) => keyWithPrincipal(["environments", { environmentId }]),
    logsToken: (environmentId?: string) => keyWithPrincipal(["environments", "logsToken", { environmentId }]),
    accessToken: (environmentId?: string) => keyWithPrincipal(["environments", "accessToken", { environmentId }]),
    gitChanges: (environmentId?: string) => keyWithPrincipal(["environments", "gitChanges", { environmentId }]),
    gitDiff: (environmentId?: string, path?: string) =>
        keyWithPrincipal(["environments", "gitDiff", { environmentId, path }]),
    fileDiffContent: (environmentId?: string, path?: string, includeBinaryContent?: boolean) =>
        keyWithPrincipal(["environments", "fileDiffContent", { environmentId, path, includeBinaryContent }]),
    readFile: (environmentId?: string, path?: string, offset?: number, length?: number) =>
        keyWithPrincipal(["environments", "readFile", { environmentId, path, offset, length }]),
};

/**
 * Checks whether the given environment ID exists in any query cache (by-ID, list, inventory, or runner).
 * Used to avoid unnecessary GetEnvironment RPCs for environments not visible to the current user.
 */
export function isEnvironmentInCache(client: QueryClient, environmentId: string): boolean {
    // Fast path: check by-ID cache
    if (client.getQueryData(environmentQueryKey.get(environmentId)) !== undefined) {
        return true;
    }

    // Check list caches (user's environments, inventory, runner)
    const listPrefixes = [
        environmentQueryKey.list().slice(0, -1),
        environmentQueryKey.listInventory({}).slice(0, -1),
        environmentQueryKey.runnerEnvironments("").slice(0, -1),
    ];
    for (const prefix of listPrefixes) {
        const entries = client.getQueriesData<{ environments?: PlainEnvironment[] }>({ queryKey: prefix });
        for (const [, data] of entries) {
            if (data?.environments?.some((e) => e.id === environmentId)) {
                return true;
            }
        }
    }

    return false;
}

export const handleEnvironmentEvent = async (api: GitpodAPI, client: QueryClient, evt: WatchEventsResponse) => {
    console.debug(`[EventHandler] Environment event:`, {
        operation: evt.operation,
        resourceId: evt.resourceId,
    });

    if (evt.operation === ResourceOperation.UPDATE) {
        if (!isEnvironmentInCache(client, evt.resourceId)) {
            console.debug(`[EventHandler] Environment ${evt.resourceId} not in cache, skipping refetch`);
            return;
        }

        const environment = await refetchEnvironment(api, evt.resourceId);
        setEnvironmentInCache(client, evt.resourceId, environment ? toPlainEnvironment(environment) : undefined);
        console.debug(`[EventHandler] Environment ${evt.resourceId} updated in cache`);
    }
    if (evt.operation === ResourceOperation.CREATE) {
        // Invalidate all environment list queries using the same prefix-based approach
        await client.invalidateQueries({ queryKey: environmentQueryKey.list().slice(0, -1) });
        await client.invalidateQueries({ queryKey: environmentQueryKey.listInventory({}).slice(0, -1) });
        await client.invalidateQueries({ queryKey: environmentQueryKey.runnerEnvironments("").slice(0, -1) });
        console.debug(`[EventHandler] Environment ${evt.resourceId} created, lists invalidated`);
    }
    if (evt.operation === ResourceOperation.DELETE) {
        setEnvironmentInCache(client, evt.resourceId, undefined);
        console.debug(`[EventHandler] Environment ${evt.resourceId} deleted from cache`);
    }
};

const refetchEnvironment = async (api: GitpodAPI, environmentId: string) => {
    try {
        const response = await api.environmentService.getEnvironment({ environmentId });
        return response.environment;
    } catch (error) {
        const isNotFound = error instanceof ConnectError && error.code === Code.NotFound;
        // this might happen if the environment was deleted in the meantime
        if (!isNotFound) {
            console.error("Failed to refetch environment", environmentId);
        }
    }
};

/**
 * Applies the passed PlainEnvironment shape to the cache, both "by id" and "list".
 * @param client
 * @param environment
 * @param optimisticUpdate Does not change the behavior, but helps to convey intend.
 */
export function setEnvironmentInCache(client: QueryClient, environmentId: string, environment?: PlainEnvironment) {
    client.setQueryData(environmentQueryKey.get(environmentId), environment);

    // Update useListEnvironments queries (with any filter)
    client.setQueriesData(
        { queryKey: environmentQueryKey.list().slice(0, -1) },
        (currentData?: { environments: PlainEnvironment[] }) => {
            if (!currentData) {
                return currentData;
            }
            return {
                ...currentData,
                environments: currentData.environments
                    .map((w) => (w.id !== environmentId ? w : environment))
                    .filter((w): w is PlainEnvironment => !!w),
            };
        },
    );

    // Update useListEnvironmentInventory queries
    const prefixOfInventoryKeys = environmentQueryKey.listInventory({}).slice(0, -1);
    client.setQueriesData(
        { queryKey: prefixOfInventoryKeys },
        (currentData?: { environments: PlainEnvironment[]; nextToken: string }) => {
            if (!currentData?.environments) {
                return currentData;
            }
            return {
                ...currentData,
                environments: currentData.environments
                    .map((w) => (w.id !== environmentId ? w : environment))
                    .filter((w): w is PlainEnvironment => !!w),
            };
        },
    );

    // Update useRunnerEnvironments queries
    const prefixOfRunnerKeys = environmentQueryKey.runnerEnvironments("").slice(0, -1);
    client.setQueriesData({ queryKey: prefixOfRunnerKeys }, (currentData?: { environments: PlainEnvironment[] }) => {
        if (!currentData) {
            return currentData;
        }
        return {
            ...currentData,
            environments: currentData.environments
                .map((w) => (w.id !== environmentId ? w : environment))
                .filter((w): w is PlainEnvironment => !!w),
        };
    });
}

export const NoGitInitializerFoundError = class extends Error {
    constructor(message: string) {
        super(message);
        this.name = "NoGitInitializerFoundError";
    }
};

type GitInitializerParams = {
    ref?: string;
    refType?: "branch" | "commit" | "tag";
};
type ParamsWithProject = {
    project: PlainProject;
    gitInitializerParams?: GitInitializerParams;
    environmentClassId?: string;
    devcontainerConfig?: EnvironmentSpec_DevContainer;
};
export const useCreateEnvironmentFromProject = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    const segmentTrack = useSegmentTrack();
    const incrementStartCount = useIncrementEnvironmentStartCount();

    return useMutation({
        onMutate: (params: ParamsWithProject) => {
            return params;
        },
        onSettled: (environment, error, _result, context) => {
            if (error) {
                segmentTrack("Environment Create Failed", { error: error.message || "Unknown error" });
            } else {
                segmentTrack("Environment Create Succeeded", { ...context, environmentId: environment?.id });
            }
        },
        mutationFn: async ({
            project,
            gitInitializerParams,
            environmentClassId,
            devcontainerConfig,
        }: ParamsWithProject): Promise<PlainEnvironment> => {
            const spec = new EnvironmentSpec({
                desiredPhase: EnvironmentPhase.RUNNING,
            });

            // Add devcontainer config if provided
            if (devcontainerConfig) {
                spec.devcontainer = devcontainerConfig;
            }

            // if an environment class is provided, set it in the machine spec
            if (environmentClassId) {
                spec.machine = new EnvironmentSpec_Machine({
                    class: environmentClassId,
                });
            }

            // if a custom ref is provided, we need to construct our own git initializer
            if (gitInitializerParams?.ref) {
                const projectWithCustomInitializer = getProjectWithCustomInitializer(project, {
                    ref: gitInitializerParams.ref,
                    refType: gitInitializerParams.refType || "branch",
                });

                if (!projectWithCustomInitializer.initializer) {
                    throw new NoGitInitializerFoundError("No git initializer found");
                }

                const content = new EnvironmentSpec_Content({
                    initializer: projectWithCustomInitializer.initializer,
                });
                spec.content = content;
            }
            const { environment } = await api.environmentService.createEnvironmentFromProject({
                projectId: project.id,
                spec,
            });
            if (!environment) {
                throw new Error("Failed to create environment");
            }

            return toPlainEnvironment(environment);
        },
        onSuccess: async () => {
            // We want to see the created environment in the list immediately.
            // We are receiving an event as well, but that's only about the individual environment.
            // matching keys by pattern like ['principal', '123', 'environments', 'list']
            await client.invalidateQueries({ queryKey: environmentQueryKey.list().slice(0, -1) }).catch(console.error);
            incrementStartCount();
        },
        meta: {
            method: "EnvironmentService.CreateEnvironmentFromProject",
        },
    });
};

type UseCheckScmAuthForProjectParams = {
    project: PlainProject;
    preferredEnvironmentClassId?: string;
    projectEnvironmentClasses: PlainProjectEnvironmentClass[];
};

export type CheckScmAuthForProjectResult = {
    authenticationResult: PlainMessage<CheckAuthenticationForHostResponse>;
    project: PlainProject;
    environmentClass: PlainRunnerEnvironmentClass;
};

export const useCheckScmAuthForProject = () => {
    const api = useGitpodAPI();
    return useMutation<CheckScmAuthForProjectResult | undefined, Error, UseCheckScmAuthForProjectParams>({
        onMutate: (params: UseCheckScmAuthForProjectParams) => {
            return params;
        },
        mutationFn: async ({
            project,
            preferredEnvironmentClassId,
            projectEnvironmentClasses,
        }: UseCheckScmAuthForProjectParams) => {
            const repoURL = getRepoUrlFromInitializer(project.initializer);
            if (!repoURL) {
                throw new Error("No context URL found");
            }
            const scmHost = new URL(repoURL).host;

            const defaultProjectClass = projectEnvironmentClasses[0]?.environmentClass;
            const defaultProjectClassId =
                defaultProjectClass?.case === "environmentClassId" ? defaultProjectClass.value : "";

            // When the default project environment class is set to "Local Runner" then we can't
            // perform the auth check up front, as we don't know which local runner will be used
            if (defaultProjectClass?.case === "localRunner" && !preferredEnvironmentClassId) {
                return undefined;
            }

            const envClassResp = await api.runnerConfigurationService.getEnvironmentClass(
                new GetEnvironmentClassRequest({
                    environmentClassId: preferredEnvironmentClassId ?? defaultProjectClassId,
                }),
            );
            const envClass = toPlainRunnerEnvironmentClass(envClassResp.environmentClass!);

            const response = await api.runnerService.checkAuthenticationForHost({
                host: scmHost,
                runnerId: envClass.runnerId,
            });
            return {
                authenticationResult: toPlainMessage(response),
                project,
                environmentClass: envClass,
            };
        },
    });
};

export type CheckScmAuthForHostResult = PlainMessage<CheckAuthenticationForHostResponse>;
export type CheckScmAuthForHostParams = {
    host: string;
    runnerId: string;
};
export const useCheckScmAuthForHost = () => {
    const api = useGitpodAPI();
    return useMutation<CheckScmAuthForHostResult | undefined, Error, CheckScmAuthForHostParams>({
        onMutate: (params: CheckScmAuthForHostParams) => {
            return params;
        },
        mutationFn: async ({ runnerId, host }: CheckScmAuthForHostParams) => {
            const response = await api.runnerService.checkAuthenticationForHost({
                host,
                runnerId,
            });
            return { ...response };
        },
    });
};

/**
 * Extracts the repository URL from the environment initializer.
 *
 * The environment initializer can contain multiple initialization specs, each with a different type of context.
 * This function searches through the specs and returns the URL from the first spec that has a "contextUrl" or "git" case.
 *
 * @param initializer The environment initializer object.
 * @returns The repository URL, or undefined if no URL could be found.
 */
export function getRepoUrlFromInitializer(initializer: PlainMessage<EnvironmentInitializer> | undefined) {
    const urls = initializer?.specs
        .map((s) => {
            switch (s.spec.case) {
                case "contextUrl":
                    return s.spec.value.url;
                case "git":
                    return s.spec.value.remoteUri;
            }
        })
        .filter((u) => !!u)
        .map((u) => u!);
    return urls?.[0];
}

export function getProjectInitializer(
    initializer: PlainMessage<EnvironmentInitializer> | undefined,
): PlainMessage<GitInitializer> | undefined {
    const matches = initializer?.specs
        .map((s) => {
            switch (s.spec.case) {
                case "git":
                    return s.spec.value;
            }
        })
        .filter((b) => !!b);
    return matches?.[0];
}

/**
 * Creates a modified copy of a project with its git initializer updated to use custom git parameters.
 * This unifies the logic for creating projects with custom branches/commits/tags used in both
 * useCreateEnvironmentFromProject and PR context handling.
 */
export function getProjectWithCustomInitializer(
    project: PlainProject,
    gitParams: { ref: string; refType: "branch" | "commit" | "tag" },
): PlainProject {
    if (!project.initializer) {
        return project;
    }

    // Get the repository URL
    const repoUrl = getRepoUrlFromInitializer(project.initializer);
    if (!repoUrl) {
        return project;
    }

    // Get the original git initializer to copy properties like checkoutLocation and upstreamRemoteUri
    const originalGitInitializer = getProjectInitializer(project.initializer);
    if (!originalGitInitializer) {
        return project;
    }

    // Create a new git initializer with the specified ref
    const targetMode =
        gitParams.refType === "branch"
            ? GitInitializer_CloneTargetMode.REMOTE_BRANCH
            : gitParams.refType === "tag"
              ? GitInitializer_CloneTargetMode.REMOTE_TAG
              : GitInitializer_CloneTargetMode.REMOTE_COMMIT;

    const newInitializer: PlainMessage<EnvironmentInitializer> = {
        specs: [
            {
                spec: {
                    case: "git",
                    value: {
                        remoteUri: repoUrl,
                        targetMode,
                        cloneTarget: gitParams.ref,
                        checkoutLocation: originalGitInitializer.checkoutLocation || "",
                        upstreamRemoteUri: originalGitInitializer.upstreamRemoteUri || "",
                    },
                },
            },
        ],
    };

    // Return a new project with the modified initializer
    return {
        ...project,
        initializer: newInitializer,
    };
}

export type UseCreateEnvironmentParamsFromContextUrl = {
    type: "contextUrl";
    contextURL: string;
    classID: string;
    devcontainerConfig?: EnvironmentSpec_DevContainer;
};
export type UseCreateBlankEnvironmentParamsFromContextUrl = {
    type: "blank";
    classID: string;
    devcontainerConfig?: EnvironmentSpec_DevContainer;
};
export const useCreateEnvironment = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    const segmentTrack = useSegmentTrack();
    const incrementStartCount = useIncrementEnvironmentStartCount();

    return useMutation({
        onMutate: (
            params: UseCreateEnvironmentParamsFromContextUrl | UseCreateBlankEnvironmentParamsFromContextUrl,
        ) => {
            return params;
        },
        onSettled: (environment, error, _result, context) => {
            if (error) {
                segmentTrack("Environment Create Failed", { error: error.message || "Unknown error" });
            } else {
                segmentTrack("Environment Create Succeeded", { ...context, environmentId: environment?.id });
            }
        },
        mutationFn: async (
            params: UseCreateEnvironmentParamsFromContextUrl | UseCreateBlankEnvironmentParamsFromContextUrl,
        ): Promise<PlainEnvironment> => {
            const initializerSpecs: PartialMessage<EnvironmentInitializer_Spec>[] =
                params.type === "contextUrl"
                    ? [
                          {
                              spec: {
                                  case: "contextUrl",
                                  value: {
                                      url: params.contextURL,
                                  },
                              },
                          },
                      ]
                    : [];

            const spec = new EnvironmentSpec({
                desiredPhase: EnvironmentPhase.RUNNING,
                machine: new EnvironmentSpec_Machine({
                    class: params.classID,
                }),
                content: new EnvironmentSpec_Content({
                    session: uuidv4(),
                    initializer: {
                        specs: initializerSpecs,
                    },
                }),
            });

            // Add devcontainer config if provided
            if (params.devcontainerConfig) {
                spec.devcontainer = params.devcontainerConfig;
            }

            const { environment } = await api.environmentService.createEnvironment({ spec });

            if (!environment) {
                throw new Error("Failed to create environment");
            }

            return toPlainEnvironment(environment);
        },
        onSuccess: async () => {
            // We want to see the created environment in the list immediately.
            // We are receiving an event as well, but that's only about the individual environment.
            // matching keys by pattern like ['principal', '123', 'environments', 'list']
            await client.invalidateQueries({ queryKey: environmentQueryKey.list().slice(0, -1) }).catch(console.error);
            incrementStartCount();
        },
        meta: {
            method: "EnvironmentService.CreateEnvironment",
        },
    });
};

export const useUpdateEnvironment = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    const { data: user } = useAuthenticatedUser();
    return useMutation({
        mutationFn: async ({ req }: { req: PartialMessage<UpdateEnvironmentRequest> }) => {
            if (!user) {
                throw new Error("Not authenticated");
            }

            const resp = await api.environmentService.updateEnvironment(new UpdateEnvironmentRequest(req));
            if (!resp) {
                throw new Error("Failed to update of the environment");
            }
            return;
        },
        onSettled: async (_, __, { req }) => {
            await client.invalidateQueries({ queryKey: environmentQueryKey.get(req.environmentId) });
        },
    });
};

export const useListEnvironments = (filter?: PartialMessage<ListEnvironmentsRequest_Filter>) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: environmentQueryKey.list(filter),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            const environments = await autoPaginate(
                (params) => api.environmentService.listEnvironments(params),
                {
                    filter: new ListEnvironmentsRequest_Filter({
                        creatorIds: [user.id],
                        ...filter,
                        // Only show default role environments (excludes prebuilds, workflows, etc.)
                        roles: [EnvironmentRole.DEFAULT],
                    }),
                },
                (response) => response.environments,
            );

            return {
                environments: environments.map(toPlainEnvironment),
            };
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: !!user,
        gcTime: 1_000 * 60 * 60, // 1 hour
        staleTime: 1_000 * 60 * 5, // 5 minutes

        // refetch on reconnect, window focus, and every 5 minutes
        refetchOnReconnect: "always",
        refetchOnWindowFocus: "always",
        refetchIntervalInBackground: true,
        refetchInterval: defaultRefetchLists,
    });
};

/**
 * Hook to check if there are any archived environments.
 */
export const useHasArchivedEnvironments = () => {
    const { data, isLoading, isPending, error } = useListEnvironments({
        archivalStatus: ListEnvironmentsRequest_ArchivalStatus.ARCHIVED,
    });

    // Filter out environments that are deleted or being deleted (same logic as useEnvironmentsByProjects)
    const filteredEnvironments =
        data?.environments.filter(
            (env) =>
                env.spec?.desiredPhase !== EnvironmentPhase.DELETED && env.status?.phase !== EnvironmentPhase.DELETING,
        ) ?? [];

    return {
        hasArchivedEnvironments: filteredEnvironments.length > 0,
        isLoading,
        isPending,
        error,
    };
};

export type UseListEnvironmentInventoryParams = {
    runnerID?: string;
    creatorID?: string;
    projectID?: string;
    phase?: EnvironmentPhase;
    runnerKind?: RunnerKind;
    createdBefore?: Date;
    token?: string;
};

export const useListEnvironmentInventory = ({
    creatorID,
    projectID,
    runnerID,
    phase,
    runnerKind,
    createdBefore,
    token = "",
}: UseListEnvironmentInventoryParams) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: environmentQueryKey.listInventory({
            creatorID,
            projectID,
            runnerID,
            phase,
            runnerKind,
            createdBefore: createdBefore ? createdBefore.getTime() : undefined,
            token,
        }),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            const resp = await api.environmentService.listEnvironments({
                pagination: {
                    token,
                },
                filter: new ListEnvironmentsRequest_Filter(
                    new ListEnvironmentsRequest_Filter({
                        creatorIds: creatorID ? [creatorID] : undefined,
                        projectIds: projectID ? [projectID] : undefined,
                        runnerIds: runnerID ? [runnerID] : undefined,
                        statusPhases: phase ? [phase] : undefined,
                        runnerKinds: runnerKind ? [runnerKind] : undefined,
                        createdBefore: createdBefore ? Timestamp.fromDate(createdBefore) : undefined,
                        // Inventory shows all environments regardless of role (including workflow environments)
                    }),
                ),
            });
            return {
                environments: resp.environments.map(toPlainEnvironment),
                nextToken: resp.pagination?.nextToken ?? "",
            };
        },
        staleTime: 2_000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: !!user,
        placeholderData: keepPreviousData,
    });
};

/**
 * Returns all environments on `runnerId` that are not DELETED
 */
export const useRunnerEnvironments = (runnerId: string) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: environmentQueryKey.runnerEnvironments(runnerId),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            const environments = await autoPaginate(
                (params) => api.environmentService.listEnvironments(params),
                {
                    filter: new ListEnvironmentsRequest_Filter(
                        new ListEnvironmentsRequest_Filter({
                            runnerIds: [runnerId],
                            statusPhases: environmentNotDeletedPhases,
                            creatorIds: [user.id],
                            // Only show default role environments (excludes prebuilds, workflows, etc.)
                            roles: [EnvironmentRole.DEFAULT],
                        }),
                    ),
                },
                (response) => response.environments,
            );

            return {
                environments: environments.map(toPlainEnvironment),
            };
        },
        staleTime: 60_000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: !!user,
    });
};

export type PlainRunnerEnvironmentClass = PlainMessage<EnvironmentClass> & { analyticsName?: string };

export const toPlainRunnerEnvironmentClass = (clazz: EnvironmentClass): PlainRunnerEnvironmentClass => {
    return toPlainMessage(clazz);
};

export type PlainEnvironmentClassList = {
    classes: PlainRunnerEnvironmentClass[];
};

export const toRunnerEnvironmentClass = (runnerEnvironmentClass: EnvironmentClass): PlainRunnerEnvironmentClass => {
    return toPlainMessage(runnerEnvironmentClass);
};

export const handleEnvironmentClassEvent = async (client: QueryClient) => {
    // Invalidate environment class queries. We cannot just update the caches, because we do not know if the class still satisfies the filter used in the query.
    await client.invalidateQueries({ queryKey: environmentQueryKey.listClasses({}).slice(0, -1) }).catch(console.error);
};

export const useListEnvironmentClasses = ({
    filter,
    enabled = true,
}: {
    filter: PartialMessage<ListEnvironmentClassesRequest_Filter>;
    enabled?: boolean;
}) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    const { data, isLoading, isPending, isRefetching, isSuccess, error, refetch } = useQuery({
        queryKey: environmentQueryKey.listClasses(filter),
        queryFn: async (): Promise<PlainEnvironmentClassList> => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const classes = await autoPaginate(
                (params) => api.environmentService.listEnvironmentClasses(params),
                {
                    filter,
                },
                (response) => response.environmentClasses,
            );

            return {
                classes: classes.map(toRunnerEnvironmentClass),
            };
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: !!user && enabled,
        staleTime: 1_000 * 60 * 5, // 5 minutes
        gcTime: 1_000 * 60 * 60 * 24 * 7, // 1 week
        // even if the stale time is not over, we want to refetch on reconnect
        refetchOnReconnect: "always",
    });
    return { data, isLoading: isLoading || isPending || isRefetching, isSuccess, error, refetch };
};

export const useEnvironment = (environmentId?: string) => {
    const api = useGitpodAPI();

    return useQuery<PlainEnvironment, Error>({
        queryKey: environmentQueryKey.get(environmentId),
        queryFn: async (): Promise<PlainEnvironment> => {
            if (!environmentId) {
                throw new Error("No environment ID provided");
            }

            const { environment } = await api.environmentService.getEnvironment({ environmentId });
            if (!environment) {
                throw new Error("Environment not found");
            }

            return toPlainEnvironment(environment);
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: !!environmentId,
        staleTime: 5_000,
    });
};

export const useCreateEnvironmentLogsToken = (environmentId?: string) => {
    const api = useGitpodAPI();
    const tokenValidDuration = 1000 * 60 * 45; // 45 minutes
    return useQuery<string, Error>({
        queryKey: environmentQueryKey.logsToken(environmentId),
        queryFn: async (): Promise<string> => {
            if (!environmentId) {
                throw new Error("No environment ID provided");
            }

            const { accessToken } = await api.environmentService.createEnvironmentLogsToken({ environmentId });
            if (!accessToken) {
                throw new Error("No token provided not found");
            }

            return accessToken;
        },
        staleTime: tokenValidDuration,
        gcTime: tokenValidDuration,
        enabled: !!environmentId,
    });
};

export const useCreateEnvironmentAccessToken = (environmentId?: string) => {
    const api = useGitpodAPI();
    const tokenValidDuration = 1000 * 60 * 45; // 45 minutes
    return useQuery<string, Error>({
        queryKey: environmentQueryKey.accessToken(environmentId),
        queryFn: async (): Promise<string> => {
            if (!environmentId) {
                throw new Error("No environment ID provided");
            }

            const { accessToken } = await api.environmentService.createEnvironmentAccessToken({ environmentId });
            if (!accessToken) {
                throw new Error("No access token provided");
            }

            return accessToken;
        },
        staleTime: tokenValidDuration,
        gcTime: tokenValidDuration,
        enabled: !!environmentId,
    });
};

type StartEnvironmentParams = {
    environmentId: string;
    skipPolicyCheck?: boolean;
};
export const useStartEnvironment = () => {
    const queryClient = useQueryClient();
    const api = useGitpodAPI();
    const segmentTrack = useSegmentTrack();
    const { data: user } = useAuthenticatedUser();
    const incrementStartCount = useIncrementEnvironmentStartCount();

    return useMutation({
        onMutate: (params: StartEnvironmentParams) => {
            const cached = queryClient.getQueryData<PlainEnvironment>(
                environmentQueryKey.get(params.environmentId),
            );
            const wasRunning =
                cached?.status?.phase === EnvironmentPhase.RUNNING &&
                cached?.spec?.desiredPhase === EnvironmentPhase.RUNNING;
            return { ...params, wasRunning };
        },
        onSettled: (_environment, error, _result, context) => {
            if (!context) {
                return;
            }
            if (error) {
                segmentTrack("Environment Start Failed", {
                    environmentId: context.environmentId,
                    error: error.message || "Unknown error",
                });
            } else {
                segmentTrack("Environment Start Succeeded", {
                    environmentId: context.environmentId,
                });
                if (!context.wasRunning) {
                    incrementStartCount();
                }
            }
        },
        mutationFn: async ({
            environmentId,
            skipPolicyCheck,
        }: StartEnvironmentParams): Promise<PlainEnvironment | undefined> => {
            if (!skipPolicyCheck) {
                const { policies } = await api.organizationService.getOrganizationPolicies({
                    organizationId: user?.organizationId,
                });
                const { environment } = await api.environmentService.getEnvironment({ environmentId });
                const maxEnvLifetime = policies?.maximumEnvironmentLifetime?.seconds
                    ? Number(policies.maximumEnvironmentLifetime.seconds)
                    : 0;
                if (maxEnvLifetime > 0) {
                    const createdAt = environment?.metadata?.createdAt?.toDate().getTime();
                    if (createdAt && Date.now() - createdAt > maxEnvLifetime * 1000) {
                        throw new MaximumEnvironmentLifetimeExceededError(
                            `Maximum environment lifetime exceeded: ${maxEnvLifetime} seconds`,
                            maxEnvLifetime,
                        );
                    }
                }
            }

            await api.environmentService.startEnvironment({
                environmentId,
            });
            const { environment } = await api.environmentService.getEnvironment({ environmentId });
            if (!environment) {
                throw new Error("Environment not found");
            }
            const env = toPlainEnvironment(environment);
            setEnvironmentInCache(queryClient, environmentId, env);
            return env;
        },
    });
};

export const useStopEnvironment = () => {
    const queryClient = useQueryClient();
    const api = useGitpodAPI();
    const segmentTrack = useSegmentTrack();

    return useMutation({
        mutationFn: async (environmentId: string): Promise<PlainEnvironment | undefined> => {
            await api.environmentService.stopEnvironment({ environmentId });
            const { environment } = await api.environmentService.getEnvironment({ environmentId });
            if (!environment) {
                throw new Error("Environment not found");
            }
            const env = toPlainEnvironment(environment);
            setEnvironmentInCache(queryClient, environmentId, env);
            return env;
        },
        onSettled: (_environment, error, _result, environmentId) => {
            if (!environmentId) {
                return;
            }
            if (error) {
                segmentTrack("Environment Stop Failed", {
                    environmentId,
                    error: error.message || "Unknown error",
                });
            } else {
                segmentTrack("Environment Stop Succeeded", {
                    environmentId,
                });
            }
        },
    });
};

export const useDeleteEnvironment = () => {
    const queryClient = useQueryClient();
    const api = useGitpodAPI();
    const segmentTrack = useSegmentTrack();

    return useMutation({
        mutationFn: async ({ environmentId, force = false }: { environmentId: string; force: boolean }) => {
            await api.environmentService.deleteEnvironment({
                environmentId,
                force,
            });

            let env: PlainEnvironment | undefined;
            // When force-deleted, the environment will not be found
            if (!force) {
                const { environment } = await api.environmentService.getEnvironment({ environmentId });
                if (!environment) {
                    throw new Error("Environment not found");
                }
                env = toPlainEnvironment(environment);
            }
            setEnvironmentInCache(queryClient, environmentId, env);
            return env;
        },
        // Handle cache updates and analytics after mutation completes (success or error)

        onSettled: async (_environment, error, params) => {
            // Trigger a refetch of the environments list to ensure UI is in sync
            await queryClient.invalidateQueries({ queryKey: ["environments-by-projects"] });

            if (!params?.environmentId) {
                return;
            }
            if (error) {
                segmentTrack("Environment Delete Failed", {
                    environmentId: params.environmentId,
                    error: error.message || "Unknown error",
                });
            } else {
                segmentTrack("Environment Delete Succeeded", {
                    environmentId: params.environmentId,
                });
            }
        },
    });
};

export const useUnarchiveEnvironment = () => {
    const queryClient = useQueryClient();
    const api = useGitpodAPI();
    const segmentTrack = useSegmentTrack();
    const { data: user } = useAuthenticatedUser();

    return useMutation<
        UnarchiveEnvironmentResponse,
        Error,
        string,
        { previousEnvironment: PlainEnvironment | undefined }
    >({
        mutationFn: async (environmentId: string) => {
            return await api.environmentService.unarchiveEnvironment({ environmentId });
        },
        onMutate: async (environmentId: string) => {
            if (!user) {
                throw new Error("Not authenticated");
            }

            // Cancel any outgoing refetches to avoid them overwriting our optimistic update
            await queryClient.cancelQueries({
                queryKey: environmentQueryKey.list().slice(0, -1),
            });

            // Snapshot previous value: get the current environment from archived environments list
            const archivedEnvironmentsData = queryClient.getQueryData<{ environments: PlainEnvironment[] }>(
                environmentQueryKey.list({ archivalStatus: ListEnvironmentsRequest_ArchivalStatus.ARCHIVED }),
            );
            const previousEnvironment = archivedEnvironmentsData?.environments.find((env) => env.id === environmentId);

            // Optimistically remove the environment from all caches (it will be re-added to active lists on success)
            setEnvironmentInCache(queryClient, environmentId, undefined);

            return { previousEnvironment };
        },
        onSettled: (_data, error, environmentId, context) => {
            if (context?.previousEnvironment) {
                if (error) {
                    // Rollback the optimistic update by restoring the environment to all caches
                    setEnvironmentInCache(queryClient, environmentId, context.previousEnvironment);
                }
            }
            // Always invalidate to ensure fresh data (handles both success and error cases)
            queryClient.invalidateQueries({ queryKey: environmentQueryKey.get(environmentId) }).catch(console.error);
            queryClient
                .invalidateQueries({ queryKey: environmentQueryKey.list().slice(0, -1), refetchType: "all" })
                .catch(console.error);

            // Analytics tracking
            if (error) {
                segmentTrack("Environment Unarchive Failed", {
                    environmentId,
                    error: error.message || "Unknown error",
                });
            } else {
                segmentTrack("Environment Unarchive Succeeded", {
                    environmentId,
                });
            }
        },
    });
};

export const useEnvironmentGitChanges = (environmentId: string) => {
    const client = useEnvironmentOpsService(environmentId);

    const getGitStatus = useCallback(async (client: EnvironmentOpsServiceClient) => {
        const response = await client.getGitStatus({});
        return toPlainMessage(response);
    }, []);

    return useQuery({
        queryKey: environmentQueryKey.gitChanges(environmentId),
        queryFn: client ? async () => await getGitStatus(client) : skipToken,
    });
};

export const useEnvironmentGitDiff = (
    environmentId: string,
    {
        path,
        enabled = true,
        refetchInterval,
    }: {
        path: string;
        enabled?: boolean;
        /** Polling interval in milliseconds. Set to poll for diff changes while open. */
        refetchInterval?: number | false;
    },
) => {
    const client = useEnvironmentOpsService(environmentId);

    const getGitDiff = useCallback(
        async (client: EnvironmentOpsServiceClient) => {
            // Convert protobuf class instances to plain objects so React Query's
            // structuralSharing (replaceEqualDeep) can compare by value and preserve
            // references when content is unchanged across polls.
            const response = await client.getGitDiff({ path });
            return toPlainMessage(response);
        },
        [path],
    );

    return useQuery({
        queryKey: environmentQueryKey.gitDiff(environmentId, path),
        queryFn: client ? async () => await getGitDiff(client) : skipToken,
        enabled,
        refetchInterval,
    });
};

function uint8ToDataUrl(data: Uint8Array, mimeType: string): Promise<string | null> {
    if (data.length === 0) return Promise.resolve(null);
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error ?? new Error("FileReader failed"));
        reader.readAsDataURL(new Blob([data], { type: mimeType }));
    });
}

export type FileDiffContentData = {
    originalContent: string | null;
    newContent: string | null;
    isBinary: boolean;
};

export const useFileDiffContent = (
    environmentId: string,
    {
        path,
        enabled = true,
        refetchInterval,
        includeBinaryContent = false,
    }: {
        path: string;
        enabled?: boolean;
        /** Polling interval in milliseconds. Set to poll for content changes while open. */
        refetchInterval?: number | false;
        /** When true, requests binary content from the server (e.g. for image diffs) */
        includeBinaryContent?: boolean;
    },
) => {
    const client = useEnvironmentOpsService(environmentId);
    const decoder = useRef(new TextDecoder());

    const getFileDiffContent = useCallback(
        async (client: EnvironmentOpsServiceClient): Promise<FileDiffContentData> => {
            const response = await client.getFileDiffContent({ path, includeBinaryContent });

            const binaryMimeType = includeBinaryContent ? mimeTypeFromPath(path) : null;

            // For binary images, convert to base64 data URLs. These are plain
            // strings so structural sharing works naturally — identical content
            // produces identical strings across polls, and there's nothing to revoke.
            if (response.isBinary && binaryMimeType) {
                return {
                    originalContent: await uint8ToDataUrl(response.originalContent, binaryMimeType),
                    newContent: await uint8ToDataUrl(response.newContent, binaryMimeType),
                    isBinary: true,
                };
            }

            // Decode bytes to strings so React Query's structuralSharing can
            // compare by value and preserve references across polls.
            // Raw Uint8Array fields always get new references from protobuf
            // deserialization, defeating structural sharing.
            return {
                originalContent: response.originalContent ? decoder.current.decode(response.originalContent) : "",
                newContent: response.newContent ? decoder.current.decode(response.newContent) : "",
                isBinary: response.isBinary,
            };
        },
        [path, includeBinaryContent],
    );

    return useQuery({
        queryKey: environmentQueryKey.fileDiffContent(environmentId, path, includeBinaryContent),
        queryFn: client ? async () => await getFileDiffContent(client) : skipToken,
        enabled,
        refetchInterval: (query) => {
            const error = query.state.error;
            if (error instanceof ConnectError && error.code === Code.Unimplemented) {
                return false;
            }
            return refetchInterval ?? false;
        },
    });
};

export const useReadFile = (
    environmentId: string,
    {
        path,
        offset = 0,
        length = 0,
        enabled = true,
    }: {
        path: string;
        offset?: number;
        length?: number;
        enabled?: boolean;
    },
) => {
    const client = useEnvironmentOpsService(environmentId);

    const readFile = useCallback(
        async (client: EnvironmentOpsServiceClient) => {
            return await client.readFile({ path, offset: BigInt(offset), length: BigInt(length) });
        },
        [path, offset, length],
    );

    return useQuery({
        queryKey: environmentQueryKey.readFile(environmentId, path, offset, length),
        queryFn: client ? async () => await readFile(client) : skipToken,
        enabled,
    });
};
